// Apps Setup → Publish: own AAB uploads, App Factory's finished builds,
// "Submit for publishing" and the player page of a client app
// (routes/admin/client-publish.ts on the API).
import api, { ApiError, signOutOnUnauthorized } from './client';
import { API_FETCH_URL } from './base';
import { useAuthStore } from '../store/auth';

export interface UploadedBuild {
  id: string;
  /** Only an AAB can be submitted; an APK is for installing and testing. */
  build_type: 'apk' | 'aab';
  /** 'factory': built and signed by App Factory; 'upload': the owner's own AAB. */
  source: 'factory' | 'upload';
  version_name: string;
  version_code: number;
  size_bytes: number | null;
  signer_sha256: string | null;
  created_at: number;
}

export type SubmissionStatus = 'submitted' | 'in_review' | 'published' | 'rejected' | 'cancelled';

export interface Submission {
  id: string;
  build_id: string;
  status: SubmissionStatus;
  client_comment: string | null;
  client_note: string | null;
  /** Staff only — the API leaves it out for clients. */
  staff_note?: string | null;
  decided_by_email?: string | null;
  created_by_email: string | null;
  created_at: number;
  updated_at: number;
  version_name: string | null;
  version_code: number | null;
}

export const MAX_AAB_BYTES = 100 * 1024 * 1024;

export const getUploadedBuilds = (appId: string) =>
  api.get<UploadedBuild[]>(`/admin/apps-setup/client-builds/${appId}`);

/** A finished APK as a file (the API answers with an attachment). */
export const downloadBuildApk = (appId: string, buildId: string) =>
  api.blob(`/admin/apps-setup/client-builds/${appId}/${buildId}/apk`);

export const getSubmissions = (appId: string) =>
  api.get<{ submissions: Submission[]; missing: string[] }>(`/admin/apps-setup/submissions/${appId}`);

export const submitForPublishing = (appId: string, buildId: string, comment?: string) =>
  api.post<{ id: string; status: SubmissionStatus }>(`/admin/apps-setup/submissions/${appId}`, {
    build_id: buildId, comment,
  });

export const cancelSubmission = (id: string) =>
  api.post<{ id: string; status: SubmissionStatus }>(`/admin/apps-setup/submissions/${id}/cancel`, {});

export const updateSubmission = (id: string, input: { status?: SubmissionStatus; client_note?: string | null; staff_note?: string | null }) =>
  api.patch<{ id: string }>(`/admin/apps-setup/submissions/${id}`, input);

/**
 * PUTs the bundle with upload progress (fetch has none). Resolves with the
 * stored build, or rejects with the API's message — a wrong package, a lower
 * versionCode, another signing key.
 */
export function uploadAab(appId: string, file: File, onProgress: (fraction: number) => void): Promise<UploadedBuild> {
  const url = `${API_FETCH_URL}/admin/apps-setup/client-builds/${appId}`;
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    const token = useAuthStore.getState().token;
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
    xhr.onerror = () => reject(new ApiError('Upload failed — check your connection and try again', 0));
    xhr.onload = () => {
      let data: { success?: boolean; data?: UploadedBuild; error?: string } = {};
      try { data = JSON.parse(xhr.responseText); } catch { /* not JSON */ }
      if (xhr.status === 401) {
        void signOutOnUnauthorized('PUT', url, new Response(xhr.responseText, { status: 401 })).catch(() => {});
        reject(new ApiError('Unauthorized', 401));
        return;
      }
      if (xhr.status >= 200 && xhr.status < 300 && data.success && data.data) resolve(data.data);
      else reject(new ApiError(data.error || `Upload failed with status ${xhr.status}`, xhr.status, data));
    };
    xhr.send(file);
  });
}

/** The tracker campaign a client app built by App Factory opens for players; null = off. */
export interface SdkOffer { campaign_id: string | null; enabled: boolean }

export const getSdkOffer = (appId: string) => api.get<SdkOffer>(`/admin/apps-setup/sdk-offer/${appId}`);
export const setSdkOffer = (appId: string, campaignId: string | null) =>
  api.put<SdkOffer>(`/admin/apps-setup/sdk-offer/${appId}`, { campaign_id: campaignId });
